// @flow

import { createAction } from 'redux-actions';

import merge from 'deepmerge';
import qs from 'query-string';
import xws from 'api/xws/';
import type { ActionType, Thunk } from 'types/redux';
import type { IPersonInfo } from './IPersonInfo';
import type { XWSResponse } from 'api/clients/xws';
import updateProfileWs from 'api/xws/profile/profileWs';
import changePasswordWs from 'api/xws/profile/changePasswordWs';

// Selectors
import { getWebkey } from 'store/reducers/queryParams/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import { getDealerCountryCode } from 'store/reducers/dealerSettings/selectors';
import {
  getPersonInfo,
  getContactInfoProperty
} from 'store/reducers/user/personInfo/selectors';

import {
  updatePrivacyPreferences,
  updateReservationStatusPrivacyPreferences
} from 'store/reducers/appointment/actions';

// Constants
import { getService } from 'constants/ajax';

type ApptPrivacySettings = {
  emailPrivacySetting: string,
  textPrivacySetting: string
};

type PrivacyPreferences = {
  emailPrivacyPreference: string,
  textPrivacyPreference: string
};

export const SET_PERSON_INFO = 'user/personInfo/SET_PERSON_INFO';
export const REMOVE_USER_VEHICLE = 'user/personInfo/REMOVE_USER_VEHICLE';
export const UPDATE_PRIVACY_SETTINGS =
  'user/personInfo/UPDATE_PRIVACY_SETTINGS';
export const UPDATE_USER_VEHICLES = 'user/personInfo/UPDATE_USER_VEHICLE';

const setPersonInfo = createAction(
  SET_PERSON_INFO,
  // identity payload mapper checked with flow :)
  (personInfo: IPersonInfo) => personInfo
);

export const removeUserVehicle = (vehicleId: number) => {
  return {
    type: REMOVE_USER_VEHICLE,
    payload: vehicleId
  };
};

export const updateUserVehicles = (vehicle: Object) => {
  return {
    type: UPDATE_USER_VEHICLES,
    payload: vehicle
  };
};

export const updateProfile = (
  newPersonInfo: Object
): Thunk<Promise<any>> => async (dispatch, getState) => {
  const webKey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  const personInfo = getPersonInfo(getState());
  const overwriteMerge = (destinationArray, sourceArray, options) =>
    sourceArray;
  const completePersonInfo = merge(personInfo, newPersonInfo, {
    arrayMerge: overwriteMerge
  });
  const { vehicles, ...personInfoWithoutVehicles } = completePersonInfo;
  try {
    const { data } = await updateProfileWs({
      tokenId,
      webKey,
      personInfo: personInfoWithoutVehicles
    });
    dispatch(setPersonInfo(completePersonInfo));
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    return { success: false };
  }
};

export const changePassword = (
  password: string,
  newPassword: string
): Thunk<Promise<XWSResponse>> => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  const languageCode = getLocaleCode(getState()).split('_');
  const locale = getLocaleCode(getState());
  const countryCode = getDealerCountryCode(getState());
  try {
    const { data } = await changePasswordWs({
      tokenId,
      webkey,
      languageCode: languageCode[0],
      locale,
      countryCode,
      newPassword,
      password
    });
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
    return { success: false, statusCode: 0 };
  }
};

export const deleteAccount = (): Thunk<Promise<any>> => async (
  dispatch,
  getState
) => {
  const webKey = getWebkey(getState());
  const tokenId = getTokenId(getState());
  const deleteAccountService = getService('deleteAccount');
  const request = xws(deleteAccountService).post(
    deleteAccountService.getPath(),
    qs.stringify({
      webKey,
      tokenId
    })
  );
  try {
    const data = await request;
    return data;
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
  }
};

const setUpdatedPrivacySettings = (privacySettings: PrivacyPreferences) => {
  return {
    type: UPDATE_PRIVACY_SETTINGS,
    payload: privacySettings
  };
};

const formatPrivacySetting = (unformattedSetting: string, oldSetting: any) => {
  const oldSettingArray = oldSetting ? oldSetting.split(',') : null;
  return unformattedSetting
    .split(',')
    .map((setting, index) => {
      return setting === 'X' && oldSettingArray
        ? oldSettingArray[index]
        : setting;
    })
    .join(',');
};

export const updatePrivacySettings = (
  privacySettings: ApptPrivacySettings
): Thunk<any> => async (dispatch, getState) => {
  const { emailPrivacySetting, textPrivacySetting } = privacySettings;
  const oldEmailSetting = getContactInfoProperty(
    getState(),
    'emailPrivacyPreference'
  );
  const oldTextSetting = getContactInfoProperty(
    getState(),
    'textPrivacyPreference'
  );
  const formattedEmailSetting = formatPrivacySetting(
    emailPrivacySetting,
    oldEmailSetting
  );
  const formattedTextSetting = formatPrivacySetting(
    textPrivacySetting,
    oldTextSetting
  );
  const formattedPrivacySettings = {
    emailPrivacyPreference: formattedEmailSetting,
    textPrivacyPreference: formattedTextSetting
  };
  await dispatch(setUpdatedPrivacySettings(formattedPrivacySettings));
  dispatch(updatePrivacyPreferences(formattedPrivacySettings));
};

export const getCustomerPreferences = (personId: number): Thunk<any> => async (
  dispatch,
  getState
) => {
  const getPreferenceService = getService('customerPreferences');
  const webKey = getWebkey(getState());
  getPreferenceService.setPathVars({ webKey, personId });
  try {
    const {
      data: { data }
    } = await xws(getPreferenceService).get(getPreferenceService.getPath());
    dispatch(updatePrivacyPreferences({ ...data }));
    dispatch(updateReservationStatusPrivacyPreferences({ ...data }));
  } catch (err) {
    /* eslint-disable no-console */
    console.error(err);
    /* eslint-enable no-console */
  }
};

const actions = {
  setPersonInfo
};
export default actions;

export type Action = ActionType<typeof setPersonInfo>;



// WEBPACK FOOTER //
// ./src/store/reducers/user/personInfo/actions.js